import { keyOfObject } from 'base/helpers';
import { Button } from 'base/ui/button';
import { Dialog } from 'base/ui/dialog';
import { IconInfo } from 'base/ui/icons';
import { Rows } from 'base/ui/layout';
import { Tooltip } from 'base/ui/tooltip';
import { TextRegularLight, TextSmall } from 'base/ui/typography';
import classNames from 'classnames/bind';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { ProjectEditingActions } from '../editing_actions/project';
import { EditorMode } from '../editor_mode';
import { Page, Section } from '../model';
import styles from './notifications.css';
import { YOU_NEED_TO_LOG_IN } from './task_log';

const cx = classNames.bind(styles);

export function NotificationsContainer({
  children,
  offset,
}: {
  children: React.ReactNode;
  offset: 'sitemap' | 'wireframe';
}) {
  return (
    <div className={cx(styles.notifications, offset)}>
      <Rows spacing={8} xalign="end">
        {children}
      </Rows>
    </div>
  );
}

export function Notification({
  variant,
  icon: Icon,
  inProgress,
  onClick,
  tooltip,
}: {
  variant: 'ai' | 'error' | 'subtle';
  icon: React.ComponentType;
  inProgress?: boolean;
  onClick: (() => void) | undefined;
  tooltip: string;
}) {
  const ref = React.useRef<HTMLButtonElement>(null);
  return (
    <button
      className={cx(styles.notification, variant)}
      onClick={onClick}
      ref={ref}
    >
      <div className={styles.icon}>
        <Icon />
      </div>
      {inProgress && (
        <div className={styles.progressLabel}>
          <TextSmall tag="span">...</TextSmall>
        </div>
      )}
      <Tooltip anchorRef={ref} label={tooltip} placement="horizontal" />
    </button>
  );
}

export const TaskNotifications = observer(function _TaskNotifications({
  target,
  editing,
  mode,
}: {
  target: Page | Section;
  editing: ProjectEditingActions;
  mode: EditorMode;
}) {
  const { taskLog } = editing.assistant;
  const tasks = taskLog
    .tasksAffecting(target)
    .filter((t) => t.meta.onlyNotifyIn == null || t.meta.onlyNotifyIn === mode);
  const [dialog, setDialog] = React.useState<React.ReactNode>(null);

  return (
    <>
      {tasks.map((task) => (
        <Notification
          key={keyOfObject(task)}
          variant={task.completed?.success === false ? 'error' : 'ai'}
          icon={task.completed?.success === false ? IconInfo : task.meta.icon}
          inProgress={!task.completed}
          tooltip={
            task.completed?.success === false
              ? task.meta.errorTooltip
              : task.meta.thirdPersonTooltip
          }
          onClick={() => {
            if (!task.completed || task.completed.success) return;
            if (
              task.completed.errorText == YOU_NEED_TO_LOG_IN &&
              editing.session.showAuthWall
            ) {
              editing.session.showAuthWall();
              return;
            }
            setDialog(
              <ErroredTaskDialog
                title={task.meta.errorTooltip}
                errorText={task.completed.errorText}
                onClose={() => {
                  setDialog(null);
                  taskLog.removeErroredTask(task);
                }}
              />,
            );
          }}
        />
      ))}
      {dialog}
    </>
  );
});

const ErroredTaskDialog = observer(function _ErroredTaskDialog({
  title,
  errorText,
  onClose,
}: {
  title: string;
  errorText?: string;
  onClose: () => void;
}) {
  return (
    <Dialog
      title={title}
      onClose={onClose}
      buttons={
        <Button onClick={onClose} label="OK" variant="primary" size="large" />
      }
    >
      <TextRegularLight selectable color="red">
        {errorText}
      </TextRegularLight>
      <TextRegularLight>
        Try again later or contact support if the problem persists.
      </TextRegularLight>
    </Dialog>
  );
});
